import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '../../components/Button.js';
import { Field, TextareaField } from '../../components/Field.js';
import { ImagePicker } from '../../components/ImagePicker.js';
import { useI18n } from '../../i18n/index.js';
import { loadStats, suggestItemNames } from '../../lib/itemSuggestions.js';
import styles from './PickEditor.module.css';

/**
 * 폼이 내놓는 값.
 *
 * 사진을 주소가 아니라 **Blob 또는 기존 주소**로 돌려준다. 저장하는 쪽이
 * 로그인 상태면 올려서 주소를 만들고, 비로그인이면 브라우저에 담는다 —
 * 폼은 그 차이를 몰라도 된다.
 */
export interface PickDraft {
  categoryId: string;
  itemName: string;
  itemDescription?: string;
  /** 새로 고른 사진. 안 바꿨으면 null */
  imageBlob: Blob | null;
  /** 이미 있던 사진. 새로 고르지 않았고 그대로 쓸 때만 채워진다 */
  keptImageUrl: string | null;
}

interface PickEditorProps {
  categoryId: string;
  categoryName: string;
  /** 이 카테고리에 이미 있는 것. 있으면 "바뀝니다" 안내를 띄운다 */
  existing?: { itemName: string; itemDescription?: string | undefined } | undefined;
  /** 기존 사진을 화면에 띄울 주소. 로컬 Blob이면 objectURL */
  existingImageUrl?: string | null;
  onSubmit: (draft: PickDraft) => Promise<void> | void;
  /** 이미 있는 것을 지운다. 격자에는 버튼을 둘 자리가 없어 여기로 왔다 */
  onDelete?: (() => void) | undefined;
  submitting?: boolean;
  /** 저장 중 표시 문구를 바꾼다(사진 올리는 중 등) */
  submitLabel?: string | undefined;
}

/**
 * Pick 입력 폼.
 *
 * **이름만 필수다.** 사진을 강제하면 고를 것은 정해졌는데 마땅한 사진이 없어서
 * 등록을 못 하게 된다. 격자에서 사진 없는 칸은 이름이 주인공인 글자 칸으로
 * 보이므로 줄도 깨지지 않는다.
 */
export function PickEditor({
  categoryId,
  categoryName,
  existing,
  existingImageUrl = null,
  onSubmit,
  onDelete,
  submitting = false,
  submitLabel,
}: PickEditorProps) {
  const { t } = useI18n();
  const [itemName, setItemName] = useState(existing?.itemName ?? '');

  /*
   * 같은 카테고리에 남들이 써 둔 이름. 두 글자 이상 쳐야 나온다
   * (`itemSuggestions.ts` 주석 참고 — 목록을 펴면 안 되는 이유가 있다).
   *
   * 고르고 나면 감춘다. 안 그러면 방금 고른 것이 그대로 제안으로 남아,
   * 다 채운 칸 아래에 쓸모없는 줄이 붙어 있게 된다.
   */
  const [stats, setStats] = useState<Awaited<ReturnType<typeof loadStats>>>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  useEffect(() => {
    let alive = true;
    void loadStats().then((loaded) => {
      if (alive) {
        setStats(loaded);
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  const suggestions = suggestionsOpen ? suggestItemNames(stats, categoryId, itemName) : [];
  const [description, setDescription] = useState(existing?.itemDescription ?? '');
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [keptImageUrl, setKeptImageUrl] = useState<string | null>(existingImageUrl);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 새로 고른 Blob을 화면에 띄울 주소. 바뀔 때마다 옛것을 풀어준다 —
  // 안 풀면 사진을 고를 때마다 메모리에 쌓인다.
  const [blobPreview, setBlobPreview] = useState<string | null>(null);
  useEffect(() => {
    if (imageBlob === null) {
      setBlobPreview(null);
      return;
    }
    const url = URL.createObjectURL(imageBlob);
    setBlobPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageBlob]);

  const previewUrl = blobPreview ?? keptImageUrl;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const name = itemName.trim();
    const next: Record<string, string> = {};
    if (name === '') {
      next['itemName'] = t('error.required');
    }
    if (description.trim().length > 200) {
      next['itemDescription'] = t('error.tooLong');
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    await onSubmit({
      categoryId,
      itemName: name,
      ...(description.trim() === '' ? {} : { itemDescription: description.trim() }),
      imageBlob,
      keptImageUrl: imageBlob === null ? keptImageUrl : null,
    });
  };

  const replacing = existing !== undefined;

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/*
        라벨이 카테고리를 그대로 부른다 — "어떤 애니메이션인가요?".

        예전에는 "이름"이었고 자리표시자가 "예: HHKB"로 **고정**이라,
        애니메이션을 고르고도 키보드 이름을 예로 들고 있었다. 카테고리마다
        예를 따로 적을 수는 없으니(171개다) 라벨이 그 일을 하게 했다.
        자리표시자는 없앴다 — 라벨이 물었으면 빈 칸이 답할 자리다.
      */}
      <Field
        label={t('picks.itemName', { category: categoryName })}
        /*
          카테고리를 고르면 곧바로 여기에 커서를 둔다. 검색해서 "애니메이션"을
          골랐다는 건 적을 것이 이미 머리에 있다는 뜻인데, 칸을 한 번 더 눌러야
          하면 모바일에서 키보드가 늦게 올라온다.

          이미 적어둔 것을 여는 경우에는 주지 않는다 — 사진을 바꾸러 들어왔는데
          키보드가 올라와 폼을 덮는다.
        */
        autoFocus={existing === undefined}
        value={itemName}
        autoComplete="off"
        maxLength={100}
        error={errors['itemName'] ?? null}
        onChange={(event) => {
          setItemName(event.target.value);
          setSuggestionsOpen(true);
        }}
      />

      {suggestions.length > 0 ? (
        <ul className={styles.suggestions}>
          {suggestions.map((name) => (
            <li key={name}>
              <button
                type="button"
                className={styles.suggestion}
                onClick={() => {
                  setItemName(name);
                  setSuggestionsOpen(false);
                }}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <TextareaField
        label={t('picks.itemDescription')}
        value={description}
        maxLength={200}
        error={errors['itemDescription'] ?? null}
        onChange={(event) => setDescription(event.target.value)}
      />

      <ImagePicker
        previewUrl={previewUrl}
        error={errors['image'] ?? null}
        disabled={submitting}
        onPick={(blob) => {
          setImageBlob(blob);
          setErrors((current) => {
            const { image: _removed, ...rest } = current;
            return rest;
          });
        }}
        onClear={() => {
          setImageBlob(null);
          setKeptImageUrl(null);
        }}
      />

      {/*
        닫기 버튼을 따로 두지 않는다. 시트 오른쪽 위 ✕, 배경 누르기, Esc로
        이미 닫히는데 같은 일을 하는 버튼을 하나 더 두면 저장 옆에서 자리만
        차지한다.
      */}
      <div className={styles.actions}>
        <Button type="submit" fullWidth loading={submitting}>
          {submitting && submitLabel !== undefined ? submitLabel : t('common.save')}
        </Button>
      </div>

      {/*
        지우기는 **줄을 그어 따로 둔다.**
        저장 옆에 두면 눌러야 할 것을 잘못 누르고, 가운데에 작게 띄워 두면
        어디에 속한 버튼인지 알 수 없다. 선 아래는 되돌릴 수 없는 일이라는
        신호로 흔히 쓰인다.
      */}
      {replacing && onDelete !== undefined ? (
        <div className={styles.danger}>
          <Button type="button" variant="danger" fullWidth onClick={onDelete} disabled={submitting}>
            {t('picks.deleteThis')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
